import * as Sentry from "@sentry/react";
import Accordion, { Row } from "@storeblocks/accordion";
import { H3 } from "App/global-style";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { CoverageComparisonAccordion } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/CoverageComparisonAccordion";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { useTexts } from "Components/TextContext";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { ChildHealthCoverage } from "Store/interfaces/products/coverage";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products/index";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { TextLanguage } from "Store/texts/interfaces";
import { buttonTrackId } from "Utils/analytics";
import { formBooleanToBoolean } from "Utils/formUtils";
import { Form, FormikProps } from "formik";
import React from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import {
  CHILD_ABOUT_STEP,
  CHILD_PRICE_STEP,
  ChildProductDetailsFormValues,
} from "..";
import { ChildHealthElements } from "./ChildHealth/ChildHealthElements";
import { ChildCoverages } from "./FormElements/ChildCoverages";
import { ChildPriceFormValues } from "./childProductPriceInterfaces";
import { defaultCoverage } from "./childProductPriceUtils";
import { validationSchema } from "./childProductPriceValidation";

export const ChildProductPrice = (): React.ReactElement => {
  const { getText } = useTexts();
  const handleAddProductToCart = useAddProductToCartHandler(
    CHILD_PRICE_STEP.id,
  );

  const initialFormState = useFormState(CHILD_PRICE_STEP.id, {
    coverage: defaultCoverage(),
    childHealthCoverage: ChildHealthCoverage.CHILD_HEALTH_TREATMENT,
    buyChildHealthInsurance: false,
  });

  const [
    {
      formText: { checkPrice },
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.CHILD));

  const { priceCalculation, addProductToCart } = useSelector(
    (state: ReduxState) => state,
  );

  const aboutChild = priceCalculation.priceCalculationFormStates[
    CHILD_ABOUT_STEP.id
  ] as ChildProductDetailsFormValues;

  const handleSubmit = (formProps: ChildPriceFormValues): void => {
    const productsToAdd = [];
    productsToAdd.push({
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.CHILD,
    });
    if (
      formProps.buyChildHealthInsurance &&
      formProps.childHealthCoverage &&
      formBooleanToBoolean(formProps.ergoConsent)
    ) {
      productsToAdd.push({
        coverage: formProps.childHealthCoverage,
        productType: INSURANCE_NAME_VALUE.CHILD_HEALTH,
      });
    }
    handleAddProductToCart(formProps, productsToAdd);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.CHILD}`,
        );
      }}
    >
      <ProductPriceTile
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={CHILD_PRICE_STEP.title}
      >
        {(formProps: FormikProps<ChildPriceFormValues>) => {
          return (
            <Form>
              <H3>
                {aboutChild?.childFirstName ? (
                  <>
                    {`${checkPrice.nameHeader[lang]} `}
                    <span className="data-hj-suppress">
                      {aboutChild.childFirstName}
                    </span>
                  </>
                ) : (
                  checkPrice.noNameHeader[lang]
                )}
              </H3>
              <ChildCoverages />
              <Accordion borderTop={false}>
                <Row
                  id="product-price__comparison-button"
                  header={checkPrice.comparison[lang]}
                  data-trackid={buttonTrackId(
                    checkPrice.comparison[TextLanguage.ENGLISH],
                  )}
                  headerProps={{ type: "button" }}
                >
                  <CoverageComparisonAccordion
                    product={ProductBackendName.CHILD}
                    selected={formProps.values.coverage}
                  />
                </Row>
                <Row
                  id="product-price__child_alreadyhaveinsurance-question"
                  header={getText("childform.info.alreadyhaveinsurance.title")}
                  data-trackid={buttonTrackId(
                    "product-price__child_existing-insurance-question",
                  )}
                  headerProps={{ type: "button" }}
                >
                  <ReactMarkdown linkTarget="_blank">
                    {getText("childform.info.alreadyhaveinsurance.content")}
                  </ReactMarkdown>
                </Row>
              </Accordion>
              <ChildHealthElements
                formProps={formProps}
                priceIsLoading={
                  priceCalculation.priceCalculationResult.isLoading
                }
                childName={aboutChild.childFirstName}
              />
              {addProductToCart.error && (
                <SystemErrorAlert alertId="addProductToCart" marginTop="32px" />
              )}
              <StyledComparisonWrap>
                <NavigationButtons
                  isUpsell
                  isLoading={addProductToCart.isLoading}
                />
              </StyledComparisonWrap>
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.CHILD}
                stepId={CHILD_PRICE_STEP.id}
              />
            </Form>
          );
        }}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
